import { useCallback, useEffect, useReducer } from 'react'
import type { IAlarm } from '@obvious.tech/constellation'

import api, { TEntity } from 'src/utils/api'
import type { TAlarmReportProps } from 'src/parser/shared/PDF/Provider'

import { ALARM_TIMEOUT_MS } from '../Export/multiAlarmArchive'
import type { TExportFailure } from '../Export/exportErrors'

const getAlarmByID = api.getResourceByID<TEntity<IAlarm>>('alarms/api/Alarm')

/** Matches the alarm list page size, so a full page can be selected at once. */
export const MAX_CONSOLIDATED_ALARMS = 25

const NO_ENTITIES: Array<TEntity<IAlarm>> = []
const NO_REPORTS: TAlarmReportProps[] = []
const NO_FAILURES: TExportFailure[] = []

const messageOf = (cause: unknown): string =>
  cause instanceof Error ? cause.message : JSON.stringify(cause)

type TLoaderState = {
  key: string
  fetched: boolean
  expired: boolean
  order: string[]
  entities: Array<TEntity<IAlarm>>
  loadFailures: TExportFailure[]
  collected: Map<string, TAlarmReportProps>
  reports: TAlarmReportProps[]
}

type TLoaderAction =
  | {
      type: 'LOADED'
      key: string
      order: string[]
      entities: Array<TEntity<IAlarm>>
      loadFailures: TExportFailure[]
    }
  | { type: 'REPORT'; key: string; report: TAlarmReportProps }
  | { type: 'EXPIRED'; key: string }

const INITIAL_STATE: TLoaderState = {
  key: ' none',
  fetched: false,
  expired: false,
  order: [],
  entities: NO_ENTITIES,
  loadFailures: NO_FAILURES,
  collected: new Map(),
  reports: NO_REPORTS,
}

const orderReports = (
  order: string[],
  collected: Map<string, TAlarmReportProps>,
): TAlarmReportProps[] =>
  order
    .map(id => collected.get(id))
    .filter((report): report is TAlarmReportProps => report !== undefined)

const loaderReducer = (
  state: TLoaderState,
  action: TLoaderAction,
): TLoaderState => {
  switch (action.type) {
    case 'LOADED':
      return {
        key: action.key,
        fetched: true,
        expired: false,
        order: action.order,
        entities: action.entities,
        loadFailures: action.loadFailures,
        collected: new Map(),
        reports: NO_REPORTS,
      }

    case 'REPORT': {
      if (action.key !== state.key) return state

      const id = action.report.entity.id
      if (state.collected.has(id)) return state

      const collected = new Map(state.collected)
      collected.set(id, action.report)

      return {
        ...state,
        collected,
        reports: orderReports(state.order, collected),
      }
    }

    case 'EXPIRED':
      return action.key === state.key ? { ...state, expired: true } : state
  }
}

export type TUseAlarmReportPropsResult = {
  status: 'loading' | 'ready'
  entities: Array<TEntity<IAlarm>>
  reports: TAlarmReportProps[]
  failures: TExportFailure[]
  onReady: (report: TAlarmReportProps) => void
}

export const useAlarmReportProps = (
  ids: string[],
  authorization?: string,
): TUseAlarmReportPropsResult => {
  const [state, dispatch] = useReducer(loaderReducer, INITIAL_STATE)
  const key = ids.join(',')

  useEffect(() => {
    let cancelled = false

    const load = async (): Promise<void> => {
      const order = key.length === 0 ? [] : key.split(',')

      const responses = await Promise.all(
        order.map(async id => ({
          id,
          response: await getAlarmByID({ id, token: authorization }),
        })),
      )
      if (cancelled) return

      const entities: Array<TEntity<IAlarm>> = []
      const loadFailures: TExportFailure[] = []

      responses.forEach(({ id, response }) => {
        if (response._tag === 'failure') {
          loadFailures.push({
            code: 'REPORT_DATA_INCOMPLETE',
            alarmId: id,
            cause: messageOf(response.error),
          })
          return
        }

        entities.push(response.data)
      })

      dispatch({ type: 'LOADED', key, order, entities, loadFailures })
    }

    load().catch(() => undefined)

    const timer = setTimeout(() => {
      if (!cancelled) dispatch({ type: 'EXPIRED', key })
    }, ALARM_TIMEOUT_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [key, authorization])

  const onReady = useCallback(
    (report: TAlarmReportProps) => dispatch({ type: 'REPORT', key, report }),
    [key],
  )

  const current = state.key === key && state.fetched

  if (!current)
    return {
      status: 'loading',
      entities: NO_ENTITIES,
      reports: NO_REPORTS,
      failures: NO_FAILURES,
      onReady,
    }

  const ready =
    state.entities.length === 0 ||
    state.reports.length >= state.entities.length ||
    state.expired

  const timedOut = ready
    ? state.entities
        .filter(entity => !state.collected.has(entity.id))
        .map(entity => ({
          code: 'UNKNOWN' as const,
          alarmId: entity.id,
          cause: 'the report timed out',
        }))
    : []

  return {
    status: ready ? 'ready' : 'loading',
    entities: state.entities,
    reports: state.reports,
    failures:
      timedOut.length === 0
        ? state.loadFailures
        : [...state.loadFailures, ...timedOut],
    onReady,
  }
}

export default useAlarmReportProps
